import { Alert, Card, Center, Loader, Switch, Table, Title } from '@mantine/core';
import { useMemo, useState } from 'react';
import type { NotificationCategory, NotificationChannel } from '../api/notifications';
import {
  useNotificationPreferences,
  useSetNotificationPreferences,
} from '../api/notification-preferences';

const CHANNELS: NotificationChannel[] = ['email', 'slack', 'webhook'];
const CATEGORIES: NotificationCategory[] = [
  'transactional',
  'clinical',
  'operational',
  'marketing',
];

interface NotificationPreferencesCardProps {
  userId: string;
  orgId: string;
}

export function NotificationPreferencesCard({ userId, orgId }: NotificationPreferencesCardProps) {
  const prefs = useNotificationPreferences(userId);
  const setPrefs = useSetNotificationPreferences(userId);
  const [error, setError] = useState<string>('');

  const lookup = useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const p of prefs.data ?? []) {
      map[`${p.channel}:${p.category}`] = p.optedOut;
    }
    return map;
  }, [prefs.data]);

  function handleToggle(channel: NotificationChannel, category: NotificationCategory) {
    const current = lookup[`${channel}:${category}`] ?? false;
    const allPrefs = CHANNELS.flatMap((ch) =>
      CATEGORIES.map((cat) => ({
        channel: ch,
        category: cat,
        optedOut: ch === channel && cat === category ? !current : (lookup[`${ch}:${cat}`] ?? false),
      })),
    );
    setPrefs.mutate(
      { orgId, preferences: allPrefs },
      {
        onError: (err: unknown) => {
          const apiErr = err as { status?: number; body?: unknown };
          setError(
            `Error ${apiErr.status}: ${typeof apiErr.body === 'string' ? apiErr.body : JSON.stringify(apiErr.body)}`,
          );
        },
      },
    );
  }

  return (
    <Card withBorder>
      <Title order={4} mb="md">
        Notification preferences
      </Title>
      {error && (
        <Alert color="red" mb="md" role="alert">
          {error}
        </Alert>
      )}
      {prefs.isLoading ? (
        <Center>
          <Loader size="sm" />
        </Center>
      ) : (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th />
              {CATEGORIES.map((cat) => (
                <Table.Th key={cat} style={{ textTransform: 'capitalize' }}>
                  {cat}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {CHANNELS.map((channel) => (
              <Table.Tr key={channel}>
                <Table.Td style={{ textTransform: 'capitalize', fontWeight: 500 }}>
                  {channel}
                </Table.Td>
                {CATEGORIES.map((category) => (
                  <Table.Td key={category}>
                    <Switch
                      checked={lookup[`${channel}:${category}`] ?? false}
                      onChange={() => handleToggle(channel, category)}
                      disabled={setPrefs.isPending}
                      aria-label={`${channel} ${category} opted out`}
                      data-testid={`pref-${channel}-${category}`}
                    />
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Card>
  );
}
